import { authStore } from '../state/auth.js';

const API_BASE = '/api';

interface ApiOptions extends RequestInit {
  params?: Record<string, string | number | undefined>;
}

export async function apiClient<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE}${path}`;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const isFormData = fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    // Don't set Content-Type for FormData — browser sets multipart boundary
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add CSRF token for mutating requests
  if (fetchOptions.method && fetchOptions.method !== 'GET' && fetchOptions.method !== 'HEAD') {
    const token = authStore.getState().csrfToken;
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: 'Unknown error' }));
    // Redirect to login on 401, but not when already on the login page
    if (response.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError(response.status, errorBody.error || errorBody.message || 'Request failed');
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Convenience methods
export const api = {
  get: <T>(path: string, params?: Record<string, string | number | undefined>) =>
    apiClient<T>(path, { method: 'GET', ...(params ? { params } : {}) }),

  post: <T>(path: string, body?: unknown) =>
    apiClient<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),

  patch: <T>(path: string, body?: unknown) =>
    apiClient<T>(path, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),

  delete: <T>(path: string) =>
    apiClient<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) => {
    // Don't set Content-Type — let browser set multipart boundary
    const headers: Record<string, string> = {};
    const token = authStore.getState().csrfToken;
    if (token) headers['X-CSRF-Token'] = token;
    return apiClient<T>(path, {
      method: 'POST',
      body: formData,
      headers,
    });
  },
};
